// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { BrandAutocomplete } from '@/brand/components/brand-autocomplete';
import { BrandResult, useBrandSearch } from '@/brand/hooks/use-brand-search';
import { FormTextField } from '@/components/form-text-field';
import { InfoOutlined } from '@/constants/hardfin-standard-icons';
import { CompanyLogo } from '@/customer/components/company-logo';
import { CreateCustomerFormData } from '@/customer/components/create-customer-form-schema';
import {
  Alert,
  Box,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Control,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';

interface CreateCustomerFormFieldsProps {
  mode: 'sparse' | 'full';
  control: Control<CreateCustomerFormData>;
  watch: UseFormWatch<CreateCustomerFormData>;
  setValue: UseFormSetValue<CreateCustomerFormData>;
  errors: FieldErrors<CreateCustomerFormData>;
}

export function CreateCustomerFormFields(props: CreateCustomerFormFieldsProps) {
  const {
    search: searchBrands,
    results: brandResults,
    isSearching,
  } = useBrandSearch();

  const name = props.watch('name');
  const domain = props.watch('domain');
  const isCustomer = props.watch('isCustomer');
  const isSupplier = props.watch('isSupplier');
  const hasNoRoles = !isCustomer && !isSupplier;

  const handleBrandSelect = (
    _event: React.SyntheticEvent,
    value: BrandResult | string | null
  ) => {
    if (value === null) {
      props.setValue('name', '');
      props.setValue('domain', null);
    } else if (typeof value === 'string') {
      props.setValue('name', value);
    } else {
      props.setValue('name', value.name);
      props.setValue('domain', value.domain);
    }
  };

  const handleNameInputChange = (
    _event: React.SyntheticEvent,
    value: string,
    reason: string
  ) => {
    props.setValue('name', value);

    if (reason === 'input') {
      searchBrands(value);
      if (domain) {
        props.setValue('domain', null);
      }
    }
  };

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box sx={{ pt: 1 }}>
          <CompanyLogo domain={domain} name={name || ' '} size="medium" />
        </Box>

        <Stack spacing={2} sx={{ flex: 1 }}>
          <BrandAutocomplete
            name="name"
            label="Name"
            placeholder="Enter company name"
            options={brandResults}
            inputValue={name}
            onInputChange={handleNameInputChange}
            onChange={handleBrandSelect}
            isSearching={isSearching}
            error={!!props.errors.name}
            helperText={props.errors.name?.message}
          />

          <FormTextField
            name="domain"
            label="Domain"
            placeholder="example.com"
            control={props.control}
          />
        </Stack>
      </Stack>

      <Stack spacing={1}>
        <Typography variant="subtitle2">Company roles</Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <FormControlLabel
            control={
              <Switch
                checked={isCustomer}
                onChange={(e) => props.setValue('isCustomer', e.target.checked)}
              />
            }
            label="Customer"
          />
          <Tooltip
            arrow
            placement="right"
            title="Companies with the customer role appear automatically in project and fulfillment menus"
          >
            <InfoOutlined fontSize="small" color="action" />
          </Tooltip>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <FormControlLabel
            control={
              <Switch
                checked={isSupplier}
                onChange={(e) => props.setValue('isSupplier', e.target.checked)}
              />
            }
            label="Supplier"
          />
          <Tooltip
            arrow
            placement="right"
            title="Companies with the supplier role appear automatically in procurement menus"
          >
            <InfoOutlined fontSize="small" color="action" />
          </Tooltip>
        </Stack>
        {hasNoRoles && (
          <Alert severity="warning">
            It is recommended to select at least one role. Companies without a
            role have limited functionality.
          </Alert>
        )}
      </Stack>

      {props.mode === 'full' && (
        <Stack spacing={1}>
          <Typography variant="subtitle2">Billing details</Typography>
          <Paper variant="outlined" sx={{ padding: 2 }}>
            <Stack spacing={2}>
              <Typography variant="body2">Billing address</Typography>
              <FormTextField
                fullWidth
                isMultiline
                minimumRows={3}
                name="billingAddress"
                label="Billing address"
                control={props.control}
              />
              <Stack spacing={2}>
                <Typography variant="body2">Billing contact</Typography>
                <FormTextField
                  fullWidth
                  name="billingContact.name"
                  label="Contact name"
                  control={props.control}
                />
                <FormTextField
                  fullWidth
                  name="billingContact.email"
                  label="Contact email"
                  control={props.control}
                />
                <FormTextField
                  fullWidth
                  name="billingContact.phone"
                  label="Contact phone"
                  control={props.control}
                />
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      )}
    </Stack>
  );
}
